import Lucide from "@/components/Base/Lucide";
import React, { useEffect, useState } from "react";
import ProtectedRoute from "@/components/ProtectedRoute";
import { getToken, getRole } from "@/utils/auth";
import RoleGate from "@/components/RoleGate";
// import users from "@/fakers/users"; // Disabled faker profiles
import Button from "@/components/Base/Button";
import _ from "lodash";
import { useSearchParams, useNavigate } from "react-router-dom";


type Project = {
    id: string;
    name: string;
    companyName?: string;
    status?: string;
    createdAt?: string;
    startDate?: string;
    endDate?: string;
};

export default function Projects() {
    const [data, setData] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const role = getRole();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get("companyId");

    useEffect(() => {
        (async () => {
            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                let url = `${apiUrl}/projects`;
                // For ADMIN users, we don't need to add companyId as the backend will handle it
                // For SUPERADMIN users, we can filter by companyId if provided
                if (role === "SUPERADMIN" && companyId) {
                    url += `?companyId=${companyId}`;
                }

                console.log('Fetching projects with URL:', url);
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                
                if (!res.ok) {
                    throw new Error('Failed to fetch projects');
                }

                const json = await res.json();
                setData(json.data ?? []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [companyId]);


    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex items-center h-10">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Projects
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => navigate("/projects/add")}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Add New Project
                                </Button>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-12 gap-x-6 gap-y-10 mt-3.5">
                            {data.map((p) => (
                                <div
                                    key={p.id}
                                    className="flex flex-col col-span-12 gap-x-6 gap-y-10 md:col-span-6 xl:col-span-4"
                                >
                                    <div className="p-5 box box--stacked">
                                        <div className="flex items-center pb-5 mb-5 border-b border-dashed">
                                            <div className="mr-auto">
                                                <div className="text-base font-medium">{p.name}</div>
                                                <div className="text-slate-500 mt-0.5">
                                                    Created on: {p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-MY", {
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                    }) : "-"}
                                                    </div>
                                            </div>
                                            <div>
                                                <div className="w-14 h-14 overflow-hidden rounded-full image-fit border-[3px] border-slate-200/70 bg-slate-200 flex items-center justify-center">
                                                    <Lucide icon="User" className="w-8 h-8 text-slate-400" />
                                                    {/* Faker profile photo disabled */}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center mb-7">
                                            <div className="mr-auto">
                                                <div className="text-slate-500 mt-0.5 truncate max-w-[9rem] md:max-w-none">
                                                    Start Date: {p.startDate ? new Date(p.startDate).toLocaleDateString("en-MY") : "-"}
                                                </div>
                                                <div className="text-slate-500 mt-0.5 truncate max-w-[9rem] md:max-w-none">
                                                    End Date: {p.endDate ? new Date(p.endDate).toLocaleDateString("en-MY") : "-"}
                                                </div>
                                            </div>
                                            <div className="text-base font-medium text-primary">
                                                {p.status || ""}
                                            </div>
                                        </div>
                                        <Button
                                            rounded
                                            type="button"
                                            variant="primary"
                                            className="w-full"
                                            onClick={() => {
                                                const projectId = p.id;
                                                const companyId = new URLSearchParams(window.location.search).get("companyId");
                                                const projectUrl = companyId
                                                    ? `/projects/${projectId}/sales?companyId=${companyId}`
                                                    : `/projects/${projectId}/sales`;
                                                navigate(projectUrl);
                                            }}
                                        >
                                            Enter
                                        </Button>
                                        <Button
                                            rounded
                                            type="button"
                                            variant="outline-secondary"
                                            className="w-full mt-2"
                                            onClick={() => navigate(`/projects/${p.id}/edit`)}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}
